import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from './useAuth';

const API_BASE = '/api';

interface AuditEntry {
  id: string;
  action: string;
  metadata: Record<string, any> | null;
  created_at: string;
  actor_name: string | null;
  document_title: string | null;
}

const ACTION_LABELS: Record<string, string> = {
  create_document: 'Uploaded Document',
  update_document: 'Updated Document',
  delete_document: 'Removed Document',
  restore_document: 'Restored Version',
  invite_user: 'Invited User',
  deactivate_user: 'Deactivated User',
  reactivate_user: 'Reactivated User',
  reset_password: 'Reset Password',
};

function formatMetadata(action: string, metadata: Record<string, any> | null): string {
  if (!metadata) return '-';

  switch (action) {
    case 'create_document':
      return `Uploaded as "${metadata.title || 'Untitled'}"${
        metadata.reference_code ? ` (${metadata.reference_code})` : ''
      }${metadata.via_bulk_import ? ' — via bulk import' : ''}`;

    case 'update_document':
      return `New version${metadata.version_number ? ` (v${metadata.version_number})` : ''}${
        metadata.change_notes ? ` — "${metadata.change_notes}"` : ''
      }${metadata.via_bulk_update ? ' — via bulk update' : ''}`;

    case 'delete_document':
      return 'Document removed';

    case 'restore_document':
      return 'Restored to a previous version';

    case 'invite_user':
      return `Invited ${metadata.invited_name || ''} (${metadata.invited_email || ''})`;

    case 'deactivate_user':
    case 'reactivate_user':
    case 'reset_password':
      return 'Action applied to a user account';

    default:
      return Object.entries(metadata)
        .map(([key, value]) => `${key}: ${value}`)
        .join(', ');
  }
}

function AuditLogPage() {
  const { user, checked } = useAuth();
  const navigate = useNavigate();
  const [entries, setEntries] = useState<AuditEntry[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (checked && !user) {
      navigate('/admin');
    }
  }, [checked, user, navigate]);

  useEffect(() => {
    if (!user) return;
    fetch(`${API_BASE}/admin/audit-log`, { credentials: 'include' })
      .then((res) => res.json())
      .then((data) => setEntries(Array.isArray(data) ? data : []))
      .finally(() => setLoading(false));
  }, [user]);

  if (!checked || !user) {
    return <div className="app"><p>Checking access...</p></div>;
  }

  return (
    <div className="app">
      <div className="top-bar">
        <Link to="/" className="back-button">&larr; Back to Categories</Link>
      </div>

      <h1>Audit Log</h1>

      {loading && <p>Loading...</p>}

      <div className="audit-table-wrapper">
        <table className="doc-table">
          <thead>
            <tr>
              <th>Action</th>
              <th>Document</th>
              <th>Performed By</th>
              <th>Date</th>
              <th>Details</th>
            </tr>
          </thead>
          <tbody>
            {entries.map((entry) => (
              <tr key={entry.id}>
                <td>{ACTION_LABELS[entry.action] || entry.action}</td>
                <td>{entry.document_title || '-'}</td>
                <td>{entry.actor_name || '-'}</td>
                <td>{new Date(entry.created_at).toLocaleString()}</td>
                <td>{formatMetadata(entry.action, entry.metadata)}</td>
              </tr>
            ))}
            {entries.length === 0 && !loading && (
              <tr><td colSpan={5}>No audit entries yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default AuditLogPage;